import { FormSkeleton } from "components/PortalLoading";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Admin2 from "layouts/Admin2";
import useCreateBulkImport from "api/BulkBookImport/useCreateBulkImport";
import useShowBulkImport from "api/BulkBookImport/useShowBulkImport";
import useCancelBulkImport from "api/BulkBookImport/useCancelBulkImport";
import useConfirmBulkImport from "api/BulkBookImport/useConfirmBulkImport";
import WizardStepper from "./WizardStepper";
import SelectModeStep from "./steps/SelectModeStep";
import DownloadBookListStep from "./steps/DownloadBookListStep";
import UploadBookListStep from "./steps/UploadBookListStep";
import UploadFilesStep from "./steps/UploadFilesStep";
import SubmittedStep from "./steps/SubmittedStep";
import SummaryStep from "./steps/SummaryStep";
import StatusBanner from "./StatusBanner";
import CancelImportDialog from "./CancelImportDialog";
import { WizardNavHost } from "./StepPanel";
import useDownloadBulkImportReport from "api/BulkBookImport/useDownloadBulkImportReport";
import { BsDownload } from "react-icons/bs";

const TERMINAL = ["completed", "completed_with_errors", "cancelled", "failed"];

const IN_FLIGHT = ["validating", "ready", "processing"];

const SUBMITTED_STATUSES = [...IN_FLIGHT, ...TERMINAL];

const CANCELLABLE = [
	"draft",
	"uploading",
	"validating",
	"validation_failed",
	"ready",
	"processing",
];

const LoadingSkeleton = () => <FormSkeleton label="Loading batch details and uploaded files…" />;

const ValidationFailedBanner = ({ importId }) => {
	const download = useDownloadBulkImportReport();

	return (
		<StatusBanner
			tone="warning"
			title="Validation found issues"
			action={
				<button
					type="button"
					className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs font-semibold text-amber-900 hover:bg-amber-50 disabled:opacity-50"
					disabled={download.isLoading || !importId}
					onClick={() => download.mutate(importId)}
				>
					<BsDownload className="h-3.5 w-3.5" aria-hidden />
					{download.isLoading ? "Downloading…" : "Download report"}
				</button>
			}
		>
			Download the report to see which rows failed, fix your book list or add the missing files,
			then submit again.
		</StatusBanner>
	);
};

const BulkBookImportWizardInner = ({ id }) => {
	const history = useHistory();
	const create = useCreateBulkImport();
	const cancel = useCancelBulkImport();
	const submit = useConfirmBulkImport();

	const [phase, setPhase] = useState(1);
	const [hydrated, setHydrated] = useState(false);
	const [submittedLocally, setSubmittedLocally] = useState(false);
	const [shouldPoll, setShouldPoll] = useState(false);
	const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

	const {
		data: importData,
		isLoading,
		refetch,
		isError,
		error,
	} = useShowBulkImport(id, {
		enabled: Boolean(id),
		refetchInterval: shouldPoll ? 4000 : false,
	});

	const status = importData?.status;
	const uploads = importData?.uploads || [];
	const manifestUpload = uploads.find((u) => u.type === "manifest");
	const mediaUploads = uploads.filter((u) => u.type === "cover" || u.type === "pdf");
	const validationFailed = status === "validation_failed";

	useEffect(() => {
		setShouldPoll(Boolean(status && IN_FLIGHT.includes(status)));
	}, [status]);

	useEffect(() => {
		if (!id || !status || hydrated) return;

		if (validationFailed) {
			setPhase(mediaUploads.length > 0 ? 3 : manifestUpload ? 2 : 1);
			setSubmittedLocally(false);
		} else if (SUBMITTED_STATUSES.includes(status)) {
			setPhase(4);
			setSubmittedLocally(true);
		} else if (mediaUploads.length > 0) {
			setPhase(3);
		} else if (manifestUpload) {
			setPhase(2);
		} else {
			setPhase(1);
		}
		setHydrated(true);
	}, [id, status, hydrated, manifestUpload, mediaUploads.length, validationFailed]);

	useEffect(() => {
		if (!status) return;

		if (validationFailed) {
			setSubmittedLocally(false);
			setPhase((prev) => (prev === 4 ? 3 : prev));
			return;
		}

		if (SUBMITTED_STATUSES.includes(status)) {
			setPhase(4);
			setSubmittedLocally(true);
		}
	}, [status, validationFailed]);

	useEffect(() => {
		if (!id || !isError) return;
		const code = error?.response?.status;
		if (code === 404) {
			history.replace("/bulk-book-imports");
		}
	}, [id, isError, error, history]);

	const startAttach = async () => {
		const created = await create.mutateAsync({
			mode: "attach_missing_files",
		});
		if (created?.id) {
			setPhase(1);
			setHydrated(true);
			setSubmittedLocally(false);
			history.push(`/bulk-book-imports/${created.id}`);
		}
	};

	const handleSubmit = async () => {
		await submit.mutateAsync(id);
		setSubmittedLocally(true);
		setPhase(4);
		refetch();
	};

	const refresh = () => refetch();

	const isSubmitted =
		!validationFailed && (submittedLocally || (status && SUBMITTED_STATUSES.includes(status)));

	const wizardStep = !id ? 0 : validationFailed ? phase : isSubmitted ? 4 : phase;

	const canCancel = id && importData && CANCELLABLE.includes(importData.status);

	const inWizard = wizardStep > 0;

	return (
		<Admin2>
			<div
				className={`relative${inWizard ? " portal-import-active" : ""}`}
				style={
					inWizard
						? { height: "calc(100dvh - 8rem)", overflow: "hidden" }
						: { minHeight: "calc(100vh - 4rem)" }
				}
			>
				<div className="pointer-events-none absolute inset-0" aria-hidden />

				<div
					className={`portal-import-workspace relative flex flex-col ${
						inWizard ? "h-full space-y-3" : "space-y-6"
					}`}
				>
					<div className="flex flex-shrink-0 flex-wrap items-start justify-between gap-3">
						<div className={`max-w-xl ${inWizard ? "space-y-0.5" : "space-y-2"}`}>
							{!inWizard ? (
								<p className="text-xs font-semibold uppercase tracking-widest text-akgreen">
									Publisher tools
								</p>
							) : null}
							<h1
								className={`font-semibold tracking-tight text-akgreenest ${
									inWizard ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl"
								}`}
							>
								Bulk upload book files
							</h1>
							{!inWizard ? (
								<p className="text-sm leading-relaxed text-gray-600">
									Attach missing covers and PDFs to books you already own. Submit once — processing
									continues in the background and results arrive by email.
								</p>
							) : null}
						</div>
						{canCancel ? (
							<button
								type="button"
								className="rounded-lg px-3 py-2 text-sm font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
								disabled={cancel.isLoading}
								onClick={() => setCancelDialogOpen(true)}
							>
								Cancel import
							</button>
						) : null}
					</div>

					<CancelImportDialog
						visible={cancelDialogOpen}
						onHide={() => setCancelDialogOpen(false)}
						busy={cancel.isLoading}
						onConfirm={() => {
							cancel.mutate(id, {
								onSuccess: () => {
									setCancelDialogOpen(false);
									history.push("/bulk-book-imports");
								},
							});
						}}
					/>

					<div
						className={`flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg ${
							inWizard ? "min-h-0 flex-1" : ""
						}`}
					>
						{inWizard ? (
							<div className="flex-shrink-0 border-b border-gray-100 bg-gray-50 px-5 py-3 sm:px-8">
								<WizardStepper current={wizardStep} />
							</div>
						) : null}

						{inWizard ? (
							<WizardNavHost>
								<div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-8 sm:py-5">
									{id && isLoading && !importData ? <LoadingSkeleton /> : null}

									{validationFailed && wizardStep < 4 ? (
										<div className="mb-4">
											<ValidationFailedBanner importId={id} />
										</div>
									) : null}

									{wizardStep === 1 ? (
										<DownloadBookListStep onContinue={() => setPhase(2)} />
									) : null}

									{wizardStep === 2 ? (
										<UploadBookListStep
											importId={id}
											manifestUpload={manifestUpload}
											onUploaded={refresh}
											onBack={() => setPhase(1)}
											onContinue={() => setPhase(3)}
										/>
									) : null}

									{wizardStep === 3 ? (
										<UploadFilesStep
											importId={id}
											existingUploads={mediaUploads}
											onUploaded={refresh}
											onBack={() => setPhase(2)}
											onContinue={handleSubmit}
											submitting={submit.isLoading}
											continueLabel={validationFailed ? "Submit again" : undefined}
										/>
									) : null}

									{wizardStep === 4 && TERMINAL.includes(status) ? (
										<SummaryStep importData={importData} />
									) : null}

									{wizardStep === 4 && !TERMINAL.includes(status) && !validationFailed ? (
										<SubmittedStep importData={importData} />
									) : null}
								</div>
							</WizardNavHost>
						) : (
							<div className="px-5 py-6 sm:px-8 sm:py-8">
								{wizardStep === 0 ? (
									<SelectModeStep onSelectAttach={startAttach} isStarting={create.isLoading} />
								) : null}
							</div>
						)}
					</div>
				</div>
			</div>
		</Admin2>
	);
};

const BulkBookImportWizard = () => {
	const { id } = useParams();
	return <BulkBookImportWizardInner id={id} />;
};

export default BulkBookImportWizard;
